import { EventEmitter, Injectable } from '@angular/core';
import CryptoJS from 'crypto-js';
import { JwtPayload } from 'jwt-decode';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { NodeEndPoint } from '../config/node_endpoint';
import { HttpService } from './http.service';
import { SessionService } from './session-service.service';

@Injectable({
  providedIn: 'root',
})
export class OpenAiAssistantService {
  recommendation = new EventEmitter<any>();

  constructor(
    private http: HttpService,
    private sessionService: SessionService,
  ) {}

  async createThreadMessage(body): Promise<Observable<{ question: string; answer: string; thread_id: string }>> {
    // Fetch session token first
    const { token } = await this.sessionService.getSessionToken();
    const derivedKey = this.sessionService.extractDerivedKey(token);

    // Encrypt with derived key + attach token
    const encryptedData = {
      params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
      sessionToken: token,
    };

    return new Observable<{ question: string; answer: string; thread_id: string }>((observer) => {
      try {
        fetch(`${environment.NODE_SERVER}/model-assistant-route/create-thread-message-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(encryptedData),
        })
          .then((response) => {
            if (!response.body) {
              throw new Error('Readable stream not supported in response.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            function read() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  observer.complete();
                  return;
                }

                // Convert chunk to string
                const chunkString = decoder.decode(value, { stream: true });

                // Split into lines in case multiple messages are received
                const chunkLines = chunkString.trim().split('\n');

                chunkLines.forEach((line) => {
                  try {
                    // Remove 'data: ' prefix if it exists
                    if (line.startsWith('data: ')) {
                      line = line.replace('data: ', '').trim();
                    }

                    // Ensure the line is valid JSON before parsing
                    if (line) {
                      const parsedData = JSON.parse(line); // Convert to JSON

                      // Check for question & answer fields
                      if (parsedData.question && parsedData.answer) {
                        observer.next(parsedData); // Emit { question, answer }
                      }
                      if (parsedData.done) {
                        observer.next(parsedData); // Emit { question, answer }
                      }
                    }
                  } catch (e) {
                    console.warn('Error parsing JSON chunk:', line, e);
                  }
                });

                read(); // Continue reading
              });
            }

            read(); // Start reading
          })
          .catch((error) => {
            observer.error(error);
          });
      } catch (error) {
        observer.error(error);
      }
    });
  }

  // runAssistant(body: any): Observable<any> {
  //   return this.http.post<any>(`${environment.NODE_SERVER}/lcap-assistant/run-assistant-for-school`, body);
  // }

  async runAssistant(body: FormData): Promise<Observable<{ response: any }> | null> {
    const plainData = {
      message: body.get('message'),
      userId: body.get('userId'),
      schoolYear: body.get('schoolYear'),
      schoolCdsCode: body.get('schoolCdsCode'),
      pageNumber: body.get('pageNumber'),
      schoolName: body.get('schoolName'),
      stream: body.get('stream'),
      schoolId: body.get('schoolId'),
    };

    // Fetch session token first
    const { token } = await this.sessionService.getSessionToken();
    const derivedKey = this.sessionService.extractDerivedKey(token);

    // Encrypt with derived key + attach token
    const encryptedParams = {
      params: CryptoJS.AES.encrypt(JSON.stringify(plainData), derivedKey).toString(),
      sessionToken: token,
    };

    return new Observable<{ response: any }>((observer) => {
      try {
        // Build new FormData with encrypted params + file
        const secureFormData = new FormData();
        secureFormData.append('params', encryptedParams.params);
        secureFormData.append('sessionToken', token); // ← add this

        const file = body.get('file');
        if (file) secureFormData.append('file', file as Blob);

        fetch(`${environment.NODE_SERVER}/lcap-assistant/run-assistant-for-school`, {
          method: 'POST',
          body: secureFormData, // No Content-Type header — browser sets it with boundary
        })
          .then((response) => {
            if (!response.body) throw new Error('Readable stream not supported in response.');

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            function read() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  observer.complete();
                  return;
                }

                const chunkString = decoder.decode(value, { stream: true });
                const chunkLines = chunkString.trim().split('\n');

                chunkLines.forEach((line) => {
                  try {
                    if (line.startsWith('data: ')) {
                      line = line.replace('data: ', '').trim();
                    }
                    if (line) {
                      const parsedData = JSON.parse(line);
                      if (parsedData.response || parsedData.end || parsedData.error) {
                        observer.next(parsedData);
                      }
                    }
                  } catch (e) {
                    console.warn('Error parsing JSON chunk:', line, e);
                  }
                });

                read();
              });
            }

            read();
          })
          .catch((error) => observer.error(error));
      } catch (error) {
        observer.error(error);
      }
    });
  }

  //   async splitQuestions(body: any) {
  //     try {
  //       // Encrypt the data
  //       const encryptedData = {
  //         params: CryptoJS.AES.encrypt(JSON.stringify(body), 'openaiParams').toString(), // Ensure `toString()` is called on a valid CryptoJS object
  //       };

  //       // Make HTTP POST request
  //       const response = await firstValueFrom(
  //         this.http.post<any>(
  //           `${environment.NODE_SERVER}
  // /model-assistant-route/get-splitted-question`,
  //           encryptedData,
  //         ),
  //       );

  //       return response;
  //     } catch (error) {
  //       throw new Error(`Failed to split questions: ${error.message}`);
  //     }
  //   }

  async splitQuestions(body): Promise<Observable<{ response: any }>> {
    // Fetch session token first
    const { token } = await this.sessionService.getSessionToken();
    const derivedKey = this.sessionService.extractDerivedKey(token);

    // Encrypt with derived key + attach token
    const encryptedData = {
      params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
      sessionToken: token,
    };
    return new Observable<{ response: any }>((observer) => {
      try {
        fetch(`${environment.NODE_SERVER}/model-assistant-route/get-splitted-question`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(encryptedData),
        })
          .then((response) => {
            if (!response.body) {
              throw new Error('Readable stream not supported in response.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            function read() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  observer.complete();
                  return;
                }

                const chunkString = decoder.decode(value, { stream: true });
                const chunkLines = chunkString.trim().split('\n');

                chunkLines.forEach((line) => {
                  try {
                    if (line.startsWith('data: ')) {
                      line = line.replace('data: ', '').trim();
                    }

                    if (line) {
                      const parsedData = JSON.parse(line);
                      if (parsedData.response || parsedData.end || parsedData.error) {
                        observer.next(parsedData);
                      }
                    }
                  } catch (e) {
                    console.warn('Error parsing JSON chunk:', line, e);
                  }
                });

                read();
              });
            }

            read();
          })
          .catch((error) => {
            observer.error(error);
          });
      } catch (error) {
        observer.error(error);
      }
    });
  }

  async addOpenAiAssistant(body: object) {
    try {
      const token = await firstValueFrom(
        this.http.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.openai_assistant}`, body),
      );
      return this.http.decryptToken(token);
    } catch (error) {
      throw error;
    }
  }
  async addMindSpace(body: object) {
    try {
      const token = await firstValueFrom(
        this.http.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.mindful_coach}`, body),
      );
      return token;
      // return this.http.decryptToken(token);
    } catch (error) {
      throw error;
    }
  }
  async updateMindSpace(id: number, body: object) {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.mindful_coach}/${id}`, body),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateOpenAIAssistant(id: number, body: object) {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.openai_assistant}/${id}`, body),
      );
      return this.http.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getOpenAIAssistantMindSpace(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.mindful_coach}`, {
          params: queryParams,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getOpenAIAssistant(queryParams: object): Promise<JwtPayload> {
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.openai_assistant}`, {
          params: queryParams,
        }),
      );
      return this.http.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async deleteAiAssistant(id: number) {
    try {
      return await firstValueFrom(
        this.http.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.openai_assistant}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async deleteMultipleThreads(body) {
    try {
      return await firstValueFrom(
        this.http.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.delete_multiple_message_threads}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async createQuestionThread(body) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model-assistant-route/create-questions-thread`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async createThreadEnglishLessonPlanPresentation(body) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(
          `${environment.NODE_SERVER}/model-assistant-route/create-english-presentation-thread`,
          encryptedData,
        ),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async createThreadEnglishLessonPlanQuestions(body) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };
      const response = await firstValueFrom(
        this.http.post<any>(
          `${environment.NODE_SERVER}/model-assistant-route/create-english-questions-thread`,
          encryptedData,
        ),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async createThreadEnglishLessonPlan(body) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };
      const response = await firstValueFrom(
        this.http.post<any>(
          `${environment.NODE_SERVER}/model-assistant-route/create-english-lesson-plan-thread`,
          encryptedData,
        ),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async GenerateAnswerThread(body) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model-assistant-route/generate-answer-thread`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async generateAnswerThreadStream(body): Promise<Observable<{ question: string; answer: string; thread_id: string }>> {
    // Fetch session token first
    const { token } = await this.sessionService.getSessionToken();
    const derivedKey = this.sessionService.extractDerivedKey(token);

    // Encrypt with derived key + attach token
    const encryptedData = {
      params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
      sessionToken: token,
    };
    return new Observable<{ question: string; answer: string; thread_id: string }>((observer) => {
      try {
        fetch(`${environment.NODE_SERVER}/model-assistant-route/generate-answer-thread-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(encryptedData),
        })
          .then((response) => {
            if (!response.body) {
              throw new Error('Readable stream not supported in response.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            function read() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  observer.complete();
                  return;
                }

                // Convert chunk to string
                const chunkString = decoder.decode(value, { stream: true });

                // Split into lines in case multiple messages are received
                const chunkLines = chunkString.trim().split('\n');

                chunkLines.forEach((line) => {
                  try {
                    // Remove 'data: ' prefix if it exists
                    if (line.startsWith('data: ')) {
                      line = line.replace('data: ', '').trim();
                    }

                    // Ensure the line is valid JSON before parsing
                    if (line) {
                      const parsedData = JSON.parse(line); // Convert to JSON

                      // Check for question & answer fields
                      if (parsedData.question && parsedData.answer) {
                        observer.next(parsedData); // Emit { question, answer }
                      }
                      if (parsedData.done) {
                        observer.next(parsedData); // Emit { question, answer }
                      }
                    }
                  } catch (e) {
                    console.warn('Error parsing JSON chunk:', line, e);
                  }
                });

                read(); // Continue reading
              });
            }

            read(); // Start reading
          })
          .catch((error) => {
            observer.error(error);
          });
      } catch (error) {
        observer.error(error);
      }
    });
  }

  async generateRecommendationOfDay(body): Promise<Observable<string>> {
    // Fetch session token first
    const { token } = await this.sessionService.getSessionToken();
    const derivedKey = this.sessionService.extractDerivedKey(token);

    // Encrypt with derived key + attach token
    const encryptedData = {
      params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
      sessionToken: token,
    };
    return new Observable<string>((observer) => {
      try {
        fetch(`${environment.NODE_SERVER}/model-assistant-route/generate-recommendation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(encryptedData),
        })
          .then((response) => {
            if (!response.body) {
              throw new Error('Readable stream not supported in response.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            function read() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  observer.complete();
                  return;
                }

                // Convert chunk to string
                const chunkString = decoder.decode(value, { stream: true });

                // Split into lines in case multiple messages are received
                const chunkLines = chunkString.trim().split('\n');

                chunkLines.forEach((line) => {
                  try {
                    // Remove 'data: ' prefix if it exists
                    if (line.startsWith('data: ')) {
                      line = line.replace('data: ', '').trim();
                    }

                    // Ensure the line is valid JSON before parsing
                    if (line) {
                      const parsedData = JSON.parse(line); // Convert to JSON
                      if (parsedData.text) {
                        observer.next(parsedData.text); // Emit only the "text" value
                      }
                    }
                  } catch (e) {
                    console.warn('Error parsing JSON chunk:', line, e);
                  }
                });

                read(); // Continue reading
              });
            }

            read(); // Start reading
          })
          .catch((error) => {
            observer.error(error);
          });
      } catch (error) {
        observer.error(error);
      }
    });
  }

  // async generateRecommendationOfDay(body) {
  //   try {
  //     const encryptedData = {
  //       params: CryptoJS.AES.encrypt(JSON.stringify(body), 'openaiParams').toString(),
  //     };
  //     const response = await firstValueFrom(
  //       this.http.post<any>(`${environment.NODE_SERVER}/model-assistant-route/generate-recommendation`, encryptedData),
  //     );

  //     return response;
  //   } catch (error) {
  //     throw error;
  //   }
  // }

  /**
   * @description Sends a mood assistant request to the backend API
   * to create or continue an OpenAI assistant conversation and
   * retrieve the AI-generated response. The request payload is
   * encrypted before being transmitted.
   * @param {any} prompt - Payload containing thread details,
   * user message, and conversation status information.
   * @returns {Promise<any>} Resolves with the assistant response
   * received from the backend API.
   */
  async createThreadMood(prompt: any): Promise<any> {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);
      const body = {
        prompt: prompt,
      };
      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model/create-mood-assistant`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async streamAssessmentFeedback(body: any): Promise<Observable<any>> {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);
      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      return new Observable<any>((observer) => {
        fetch(`${environment.NODE_SERVER}${NodeEndPoint.generate_assessment_feedback}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(encryptedData),
        })
          .then((response) => {
            if (!response.body) {
              observer.error('No response body found');
              return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            function read() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  observer.complete();
                  return;
                }

                const chunk = decoder.decode(value, { stream: true });
                observer.next(chunk);

                read();
              });
            }

            read();
          })
          .catch((error) => {
            observer.error(error);
          });
      });
    } catch (error) {
      throw error;
    }
  }
}
